<template>
  <v-dialog
    :model-value="open"
    @update:model-value="$emit('update:open', $event)"
    max-width="580"
    scrollable
  >
    <v-card>
      <v-card-title class="bg-primary d-flex align-center text-white">
        <template v-if="!isSubmitSuccess && !isAlreadyRegistered">
          <v-icon color="white" class="mr-2">mdi-account-plus</v-icon>
          Daftar Menjadi Relawan
        </template>
        <template v-else-if="isSubmitSuccess">
          <v-icon color="white" class="mr-2">mdi-check-circle</v-icon>
          Pendaftaran Berhasil
        </template>
        <template v-else>
          <v-icon color="white" class="mr-2">mdi-clipboard-text</v-icon>
          Detail Pendaftaran
        </template>
        <v-btn
          density="compact"
          class="ml-auto"
          elevation="0"
          color="transparent"
          icon="mdi-close"
          @click="onClose"
        />
      </v-card-title>

      <!-- Success Feedback View -->
      <template v-if="isSubmitSuccess">
        <v-card-text class="text-center py-8">
          <v-icon color="success" size="64" class="mb-4">
            mdi-check-circle
          </v-icon>

          <h3 class="text-h5 mb-4">Terima Kasih!</h3>

          <p class="text-body-1 mb-6">
            Pendaftaran relawan Anda telah berhasil disubmit. Mohon tunggu
            informasi penerimaan yang akan ditampilkan di halaman dashboard
            aktivitas Anda.
          </p>

          <v-btn
            color="primary"
            variant="flat"
            class="text-capitalize font-weight-bold rounded-xl"
            prepend-icon="mdi-view-dashboard"
            @click="goToDashboard"
          >
            Lihat di Dashboard Saya
          </v-btn>
        </v-card-text>
      </template>

      <!-- Already Registered View -->
      <template v-else-if="isAlreadyRegistered">
        <v-card-text class="text-center py-6">
          <v-icon color="info" size="64" class="mb-4">
            mdi-information-outline
          </v-icon>

          <h3 class="text-h5 mb-4">Anda Sudah Terdaftar</h3>

          <p class="text-body-1 mb-6">
            Anda telah mendaftar sebagai relawan untuk aktivitas ini. Untuk
            melihat status pendaftaran dan detail lebih lanjut, silakan kunjungi
            halaman Dashboard Aktivitas Saya.
          </p>

          <v-btn
            color="primary"
            variant="flat"
            class="text-capitalize font-weight-bold rounded-xl"
            prepend-icon="mdi-view-dashboard"
            @click="goToDashboard"
          >
            Lihat di Dashboard Saya
          </v-btn>
        </v-card-text>
      </template>

      <!-- Regular Form View -->
      <template v-else>
        <v-card-text>
          <v-form @submit.prevent="onSubmit">
            <p class="text-grey-darken-1 font-weight-bold text-body-1 mb-2">
              Mengapa Anda tertarik untuk menjadi relawan pada aktivitas ini?
            </p>
            <v-textarea
              v-model="interestReason"
              v-bind="interestReasonAttrs"
              :error-messages="errors.interestReason"
              color="primary"
              variant="outlined"
              density="compact"
              rows="3"
              placeholder="Tulis jawaban"
              class="mt-4"
              :disabled="isSubmitting"
            />

            <p class="text-grey-darken-1 font-weight-bold text-body-1 mb-2">
              Mengapa Anda adalah relawan yang tepat untuk aktivitas ini?
            </p>
            <v-textarea
              v-model="acceptanceReason"
              v-bind="acceptanceReasonAttrs"
              :error-messages="errors.acceptanceReason"
              color="primary"
              variant="outlined"
              density="compact"
              rows="3"
              placeholder="Tulis jawaban"
              class="mt-4"
              :disabled="isSubmitting"
            />

            <p class="text-grey-darken-1 font-weight-bold text-body-1 mb-2">
              Unggah Volunteering Resume Anda
            </p>
            <p class="text-grey-darken-1 text-caption mb-4">
              Unggah resume Anda dalam format PDF. Maksimal 5MB.
            </p>

            <!-- File Upload Area -->
            <div class="upload-container position-relative">
              <!-- Show file upload when no file is selected -->
              <div v-if="!fileData">
                <v-file-upload
                  v-model="uploadedFiles"
                  density="comfortable"
                  class="custom-file-upload"
                  color="primary"
                  accept="application/pdf"
                  title="Tarik dan jatuhkan file atau klik untuk memilih"
                  clearable
                  single-select
                  :loading="isUploading"
                  :disabled="isUploading || isSubmitting"
                  @change="handleUploadChange"
                />
              </div>

              <!-- Show preview when file is selected -->
              <div v-else class="file-preview rounded-lg border pa-4">
                <!-- File Info and Actions -->
                <div class="d-flex justify-space-between align-center">
                  <div class="text-body-2 d-flex align-center">
                    <v-icon color="success" size="small" class="mr-2"
                      >mdi-file-pdf-box</v-icon
                    >
                    <span>Resume berhasil diunggah</span>
                  </div>
                  <div>
                    <v-btn
                      size="small"
                      color="error"
                      variant="text"
                      @click="removeFile"
                      :disabled="isUploading || isSubmitting"
                    >
                      Hapus
                    </v-btn>
                    <v-btn
                      size="small"
                      color="primary"
                      variant="text"
                      @click="replaceFile"
                      :disabled="isUploading || isSubmitting"
                      class="ml-2"
                    >
                      Ganti
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>

            <v-alert
              v-if="errorMessage || fileError"
              type="error"
              variant="tonal"
              class="custom-alert my-4"
            >
              <p class="body-2 mb-0">
                {{ errorMessage || fileError }}
              </p>
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            size="small"
            text="batal"
            class="text-capitalize font-weight-bold"
            color="grey"
            @click="onClose"
            :disabled="isSubmitting"
          />
          <v-btn
            color="primary"
            variant="flat"
            size="small"
            class="text-capitalize font-weight-bold"
            @click="onSubmit"
            :loading="isSubmitting"
            :disabled="isSubmitting || isUploading || !isFormValid"
          >
            Daftar
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>

  <!-- Hidden file input for replacing the file -->
  <input
    ref="hiddenFileInput"
    type="file"
    accept="application/pdf"
    style="display: none"
    @change="handleUploadChange"
  />
</template>

<script setup lang="ts">
import { useForm } from "vee-validate";
import { useUploadFile } from "@/common/composables/useUploadFile";

interface Props {
  open: boolean;
  activityId: string;
  activitySlug?: string; // Either provide activityId or activitySlug
  isAlreadyRegistered?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  activitySlug: "",
  isAlreadyRegistered: false,
});

const emit = defineEmits(["close", "update:open", "success"]);
const router = useRouter();

const { $customFetch } = useNuxtApp();
const { uploadFile } = useUploadFile();

// State
const errorMessage = ref<string | null>(null);
const fileError = ref<string | null>(null);
const isSubmitSuccess = ref<boolean>(false);
const uploadedFiles = ref<File[]>([]);
const isUploading = ref<boolean>(false);
const isSubmitting = ref<boolean>(false);
const fileData = ref<{ uuid: string; url: string } | null>(null);
const hiddenFileInput = ref<HTMLInputElement | null>(null);

// Not needed anymore since we're not displaying application details
// const applicationDetails = ref({...});

// Create validation schema for text fields
const validationSchema = {
  interestReason: "required",
  acceptanceReason: "required",
};

// Initialize form with vee-validate
const {
  isSubmitting: formSubmitting,
  resetForm,
  handleSubmit,
  defineField,
  errors,
} = useForm({
  validationSchema,
  initialValues: {
    interestReason: "",
    acceptanceReason: "",
  },
});

// Define form fields
const [interestReason, interestReasonAttrs] = defineField("interestReason");
const [acceptanceReason, acceptanceReasonAttrs] =
  defineField("acceptanceReason");

// Computed property to check if form is valid
const isFormValid = computed(() => {
  return !!interestReason.value && !!acceptanceReason.value && !!fileData.value;
});

// Reset the entire form
const resetFormState = () => {
  resetForm();
  uploadedFiles.value = [];
  errorMessage.value = null;
  fileError.value = null;
  isSubmitSuccess.value = false;
  fileData.value = null;
};

// Handle file change and immediately upload
const handleUploadChange = async (event: Event) => {
  // Access file from the upload component
  const input = event.target as HTMLInputElement;
  if (input.files && input.files.length > 0) {
    const file = input.files[0];

    // Clear previous errors
    fileError.value = null;

    // Validate file size
    if (file.size > 5 * 1024 * 1024) {
      fileError.value = "Ukuran file melebihi batas maksimum 5MB";
      uploadedFiles.value = [];
      return;
    }

    // Validate file type
    if (file.type !== "application/pdf") {
      fileError.value = "Format file harus PDF";
      uploadedFiles.value = [];
      return;
    }

    // Start upload immediately
    isUploading.value = true;

    try {
      // Upload the file using useUploadFile
      const result = await uploadFile(file, "RESUME");
      fileData.value = result;
    } catch (error: any) {
      if (error.response?._data?.message) {
        fileError.value = error.response._data.message;
      } else {
        fileError.value = "Gagal mengunggah file. Silakan coba lagi.";
      }
      console.error("File upload error:", error);

      // Reset form state on error
      uploadedFiles.value = [];
    } finally {
      isUploading.value = false;
    }
  }
};

// Remove file
const removeFile = () => {
  fileData.value = null;
  uploadedFiles.value = [];
  fileError.value = null;
};

// Replace file function
const replaceFile = () => {
  if (hiddenFileInput.value) {
    hiddenFileInput.value.click();
  }
};

// Removed fetchApplicationDetails function as requested

// Map status codes to readable labels
const getStatusLabel = (status: string): string => {
  const statusMap: Record<string, string> = {
    ON_REGISTRATION: "Mendaftar",
    ACCEPTED: "Diterima",
    REJECTED: "Ditolak",
    ACTIVE: "Aktif bekerja",
    FINISHED: "Selesai bekerja",
    NOT_ANSWER: "Tidak dijawab",
  };

  return statusMap[status] || status;
};

// Map status to color
const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    ON_REGISTRATION: "blue",
    ACCEPTED: "success",
    REJECTED: "error",
    ACTIVE: "purple",
    FINISHED: "success",
    NOT_ANSWER: "warning",
  };

  return colorMap[status] || "grey";
};

// Handle close button action
const onClose = () => {
  if (isUploading.value || isSubmitting.value) return;

  emit("close");
  emit("update:open", false);

  // Reset state with a slight delay to avoid UI jumps
  setTimeout(() => {
    resetFormState();
  }, 300);
};

// Navigate to dashboard
const goToDashboard = () => {
  router.push("/dashboard/my-activities");
  emit("close");
  emit("update:open", false);
};

// Submit form
const onSubmit = handleSubmit(async (values) => {
  const useMockData = useRuntimeConfig().public.useMockData;

  try {
    errorMessage.value = null;
    fileError.value = null;
    isSubmitting.value = true;

    // Validate file
    if (!fileData.value) {
      fileError.value = "Mohon unggah resume Anda.";
      isSubmitting.value = false;
      return;
    }

    if (useMockData) {
      // Mock data submission
      await new Promise((resolve) => setTimeout(resolve, 1000));
      isSubmitSuccess.value = true;
      emit("success", { activityId: props.activityId });
      isSubmitting.value = false;
      return;
    }

    // API endpoint
    const endpoint = `/public-activities/${props.activitySlug || props.activityId}/submission`;

    // Prepare request body
    const payload = {
      interestReason: values.interestReason,
      acceptanceReason: values.acceptanceReason,
      resume: fileData.value.uuid,
    };

    // Submit application
    await $customFetch(endpoint, {
      method: "POST",
      body: payload,
    });

    // Handle success
    isSubmitSuccess.value = true;
    emit("success", { activityId: props.activityId });
  } catch (error: any) {
    // Handle error
    console.error("Application submission error:", error);

    if (error.response?._data?.message) {
      errorMessage.value = error.response._data.message;
    } else {
      errorMessage.value = "Terjadi kesalahan. Silakan coba lagi.";
    }
  } finally {
    isSubmitting.value = false;
  }
});

// Watch for dialog open state
watch(
  () => props.open,
  (newVal) => {
    if (newVal && !props.isAlreadyRegistered) {
      resetFormState();
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.success-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.custom-file-upload :deep(.v-field__append-inner) {
  padding-top: 8px;
}

.file-preview {
  background-color: rgba(0, 0, 0, 0.02);
  transition: all 0.3s ease;
}

.application-details {
  animation: fadeIn 0.5s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
